import os from '@edu-fe/os'
import * as get from '@ccforeverd/tool-get'
import login from '@/utils/login'

import api from './lib/api'
import { pick, invariant, appendDefault, drawSavedOrderId, removeSavedOrderId } from './lib/utils'

import h5Pay from './lib/pay-h5'
import freePay from './lib/pay-free'
import weixinPay from './lib/pay-weixin'
import aliPay from './lib/pay-ali'
import { startLoopOrder, stopLoopOrder, stopAllLoopOrder } from './lib/loop'
/*
 * 支付流程
      进入
       ↓
      判断是否登录 → 否 → login() → end
       ↓ 是
    ① 判断课程是否已购买 → 是 → hasBuyCallback() -> end
       ↓ 否 -- 以上为准备阶段
    ② 判断是否传入优惠券 → 是 → ⑤
       ↓ 否
    ③ 判断是否在购买页 → 是 → ⑤
       ↓ 否
    ④ 判断是否有可用的优惠券以及奖励金 → 是 → gotoPayment() 去支付页 -> end
       ↓ 否
    ⑤ 判断是否免费 → 是 → free() -> ⑥
       ↓ 否
      pay() 判断端
       ↓
    ⑥ 轮询订单状态 (有orderId时直接开启轮询, 没有时先通过courseId查询orderId然后轮询)
       ↘ h5支付宝支付: returnUrl 为当前页面, 后台会加上orderId, 进入页面时如果有orderId, 则开启轮询
       ↘ h5微信支付: 返回当前页面, 根据 courseId 来查询 orderId, 开启轮询
       ↘ 微信支付: 轮询
       ↘ 其他支付: 轮询
       ↓ 成功
      gotoResult(orderId) 去结果页, 在结果页中判断是否赠送
       ↓
      end
 */

/**
 * 支付
 *
 * @export
 * @class Pay
 */
export default class Pay {
  /**
   * 初始化支付参数
   * @param {Object}   params 参数
   * @param {String}   params.courseId (必须)课程id
   * @param {Number}   params.type 课程类型, 1:课程 | 2:专题 | 8:畅读卡
   * @param {Boolean}  params.payment 是否在支付页(默认false)
   * @param {Function} params.gotoPayment 去往支付页(parmas.payment 不为 true 时需传递, 若没传则直接执行支付)
   * @param {Function} params.gotoResult (必须)去往结果页, 会传入orderId
   * @param {String}   params.couponId 优惠券id
   * @param {Number}   params.price 价钱(非实际支付价钱, 用于判断)
   * @param {Boolean}  params.free 是否免费支付(默认false)
   * @param {String}   params.from 订单来源
   * @param {Number}   params.isSend 是否是赠送, 1:是 | 0:不是
   * @param {Number}   params.theme 赠送主题
   * @param {Number}   params.usedReward 是否使用奖励金
   * @param {Number}   params.payWay 下单方式, 9:支付宝 | 10:微信支付
   * @param {String}   params.thirdSession 小程序用户信息
   * @param {String}   params.token 分享唯一标示, 用于分销功能
   * @param {Function} params.hasBuyCallback 已购买回调
   * @param {Function} params.sendCallback 赠送回调(废弃)
   * @param {Function} params.successCallback 成功回调(废弃)
   * @param {Function} params.failCallback 支付失败回调
   * @param {Function} params.cancelCallback 取消支付回调
   */
  constructor (params) {
    this.params = params
    const enable =
      invariant(params.courseId, 'params.courseId required') &&
      invariant(params.gotoResult, 'params.gotoResult required') &&
      invariant(params.payment || params.gotoPayment, 'params.payment = true or params.gotoPayment required')
    if (enable) {
      this.checkLogin(() => this.pay())
    }
  }

  // 获取 wpsSid
  async getWpsSid () {
    this.wpsSid = login.getWpsSid()
    // this.wpsSid = '123456'
    this.params.wpsSid = this.wpsSid
    return this.wpsSid
  }

  // 获取 from
  getFrom () {
    if (this.params.from) {
      return this.params.from
    }
    const data = get.query()
    this.params.from = data.from || data.source || 'edu_pay'
    return this.params.from
  }

  // 判断是否已登录
  async checkLogin (callback) {
    console.log('check login')
    const wpsSid = await this.getWpsSid()
    if (!wpsSid) {
      await login.login()
      this.params.loginCallback(wpsSid)
      callback(wpsSid)
    } else {
      callback()
    }
  }

  // 判断课程是否已购买
  async checkCourse (courseId, type, wpsSid) {
    const { data } = await api.PackageEduFePay.hasBuy({ courseId, type, wpsSid })
    return data === 1
  }

  // 判断是否有可用的优惠券
  async checkCoupon (courseId, type, wpsSid) {
    const { data = [] } = await api.PackageEduFePay.coupon({ courseId, type, wpsSid })
    return data.length > 0
  }

  // 判断是否有可用奖励金
  async checkBonus (courseId) {
    const { data = {} } = await api.PackageEduFePay.bonus(courseId)()
    return Boolean(data.balance)
  }

  // 支付
  async pay () {
    // 补足默认
    appendDefault(this.params, 'type', 1)
    appendDefault(this.params, 'payment', false)
    appendDefault(this.params, 'free', false)
    appendDefault(this.params, 'isSend', false)
    appendDefault(this.params, 'market', '')
    appendDefault(this.params, 'theme', 1)
    appendDefault(this.params, 'usedReward', 0)
    appendDefault(this.params, 'returnUrl', window.location.href.split('#')[0])

    // 获取from
    this.getFrom()
    // 不为赠送课时判断是否已购买
    if (!this.params.isSend) {
      const hasBuy = await this.checkCourse(this.params.courseId, this.params.type, this.wpsSid)
      // 针对畅学卡做处理
      if (hasBuy && this.params.type !== 8) {
        return this.params.hasBuyCallback && this.params.hasBuyCallback()
      }
    }
    // 如果是充值知米 则直接跳转支付
    if (this.params.type === 12) {
      return this.pay5()
    }

    // 判断是否传入优惠券id
    if (this.params.couponId) {
      return this.pay5()
    }

    // 判断是否在支付页
    if (this.params.payment) {
      return this.pay5()
    }

    // 判断是否有可用优惠券以及奖励金, 有 gotoPayment 则判断, 没有则跳过
    if (this.params.gotoPayment) {
      const hasAvailableCoupon = await this.checkCoupon(this.params.courseId, this.params.type, this.wpsSid)
      const hasBonus = await this.checkBonus(this.params.courseId)

      if (hasAvailableCoupon || hasBonus) {
        return this.params.gotoPayment()
      }
    }

    this.pay5()
  }

  // 支付第5步, 除了 h5支付宝, 其他支付方式均会返回 orderId
  async pay5 () {
    // 5.是否是免费
    try {
      const isFreePay = this.params.free === true || this.params.price <= 0
      if (isFreePay) {
        this.resultData = await freePay(pick(this.params, [
          'couponId',
          'courseId',
          'from',
          'isSend',
          'theme',
          'type',
          'wpsSid',
          'market',
          'usedReward'
        ]))
        this.startLoop()
      } else {
        console.log('os type --' + os.type)
        switch (os.type) {
          case 'ali':
            this.resultData = await aliPay(pick(this.params, [
              'couponId',
              'courseId',
              'type',
              'payWay',
              'from',
              'isSend',
              'theme',
              'wpsSid',
              'market',
              'returnUrl',
              'usedReward',
              'failCallback',
              'cancelCallback'
            ]))
            this.startLoop()
            break
          case 'app':
          case 'browser':
            console.log(this.params.payWay)
            if (typeof this.params.payWay === 'undefined') {
              // 未选择支付方式
              alert('pay way')
              
              return this.params.gotoPayment()
            } else {
              this.resultData = await h5Pay(pick(this.params, [
                'couponId',
                'courseId',
                'type',
                'payWay',
                'from',
                'isSend',
                'theme',
                'wpsSid',
                'market',
                'returnUrl',
                'usedReward'
              ]))
              this.startLoop()
            }
            break
          case 'weixin':
            console.log('goooooooooooooooooooood')
            this.resultData = await weixinPay(pick(this.params, [
              'couponId',
              'courseId',
              'groupId',
              'from',
              'isSend',
              'theme',
              'type',
              'wpsSid',
              'market',
              'thirdSession',
              'token',
              'failCallback',
              'cancelCallback',
              'usedReward'
            ]))
            this.startLoop()
        }
      }
    } catch (e) {
      console.log(e)
    }
  }

  // 开启轮询
  startLoop () {
    // wuyingbo start loop pay pay 
    console.log(this.resultData)
    // if (this.resultData && this.resultData.orderId) {
    //   setTimeout(() => {
    //     startLoopOrder(this.resultData.orderId, 1000).then(() => this.finish())
    //   }, 500)
    // }
  }

  // 停止轮询
  stopLoop () {
    if (this.resultData && this.resultData.orderId) {
      stopLoopOrder(this.resultData.orderId)
    }
  }

  // 成功完结
  finish () {
    removeSavedOrderId()
    // this.params.complete
    this.params.gotoResult(this.resultData && this.resultData.orderId)
  }
}

/**
 * 进入支付页(即要调用 new Pay(...) 的页面)时调用, 有orderId时用于开启轮询
 *
 * @param {Function} gotoResult 去结果页
 */
Pay.enterPayment = function enterPayment (gotoResult) {
  const { fromAlipay, orderId: orderIdH5Alipay } = get.query()
  if (fromAlipay) {
    // h5支付宝支付返回时的情况
    return startLoopOrder(orderIdH5Alipay, 1000)
      .then(() => gotoResult(orderIdH5Alipay))
  }

  const orderIdH5Wxpay = drawSavedOrderId()
  if (orderIdH5Wxpay) {
    // h5微信支付后退时的情况(页面刷新)
    return startLoopOrder(orderIdH5Wxpay, 1000)
      .then(() => {
        gotoResult(orderIdH5Wxpay)
      })
  }
}

/**
 * 离开支付页(即要调用 new Pay(...) 的页面)时调用, 关闭所有订单轮询并删除存储orderId
 */
Pay.leavePayment = function leavePayment () {
  stopAllLoopOrder()
  removeSavedOrderId()
}

/**
 * 进入结果页时调用, 有orderId时用于开启轮询
 *
 * @param {Function} gotoResult 去结果页
 */
Pay.enterResult = function enterResult (gotoResult) {}
